import React, { useState, useEffect, useRef } from 'react';

const ContextualAIChat = ({ predictionData }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Add welcome message when the component mounts or predictionData changes
    const welcomeMessage = generateWelcomeMessage(predictionData);
    setMessages([{ text: welcomeMessage, sender: 'ai' }]);
  }, [predictionData]);

  const formatMessage = (text) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const generateWelcomeMessage = (data) => {
    const currentAction = data.currentAction;
    const nextAction = data.upcomingActions[0];
    const remainingTime = nextAction ? nextAction.predictedTime.toFixed(2) : "unknown";

    return `Welcome! I'm your AI assistant for this surgical procedure. 
    Currently, we are in the "${currentAction}" phase. 
    The next anticipated action is "${nextAction ? nextAction.name : 'unknown'}" 
    with an estimated time of ${remainingTime} minutes until it begins. 
    How can I assist you with the current procedure?`;
  };

  const formatPredictionData = () => {
    return `
Current Action: ${predictionData.currentAction}
Elapsed Time: ${predictionData.elapsedTime} seconds
Upcoming Actions:
${predictionData.upcomingActions.map(action => `- ${action.name}: ${action.predictedTime.toFixed(2)} minutes`).join('\n')}
    `.trim();
  };

  const constructPrompt = (userQuery) => {
    return `
You are an AI assistant helping with a surgical procedure. Here's the current status:

${formatPredictionData()}

The surgeon has asked the following question: "${userQuery}"

Please provide a concise and relevant response based on the given current context and the surgeon's query. Focus on actionable insights based on the surgical workflow recognition and anticipation data. Try to structure your response with steps and time using line breaks for clarity.
    `.trim();
  };

  const handleSend = async () => {
    if (input.trim()) {
      const userMessage = { text: input, sender: 'user' };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const response = await fetch('/.netlify/functions/anthropic-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: "claude-3-5-sonnet-20241022",
            messages: [
              { role: "user", content: constructPrompt(input) }
            ],
            max_tokens: 1024,
            temperature: 0.7,
          }),
        });

        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error}, details: ${JSON.stringify(errorData.details)}`);
          } else {
            throw new Error(`HTTP error! status: ${response.status}. Make sure you're running with 'netlify dev' to access serverless functions.`);
          }
        }

        const data = await response.json();
        const aiMessage = { text: data.content[0].text, sender: 'ai' };
        setMessages(prev => [...prev, aiMessage]);
      } catch (error) {
        console.error('Error fetching AI response:', error);
        setMessages(prev => [...prev, { text: `Error: ${error.message}`, sender: 'ai' }]);
      }

      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow overflow-y-auto p-4">
        {messages.map((message, index) => (
          <div key={index} className={`mb-2 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
            <span className={`inline-block p-2 rounded-lg ${message.sender === 'user' ? 'bg-blue-100' : 'bg-gray-100'}`}>
              {message.sender === 'ai' ? formatMessage(message.text) : message.text}
            </span>
          </div>
        ))}
        {isLoading && <div className="text-center">AI is thinking...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="p-4 border-t">
        <div className="flex">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask a question about the surgery..."
            className="flex-grow mr-2 p-2 border rounded-lg"
          />
          <button onClick={handleSend} className="p-2 bg-blue-500 text-white rounded-lg" disabled={isLoading}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContextualAIChat;